// Slide-over picker for adding contacts to a dial list.
//
// Mirrors the Attio "linker" experience: search, multi-select, commit.
// Reuses the existing SlideOver component and the contacts list API.

import { useEffect, useState } from 'react';
import type { Contact } from '@personical/cc-shared-types';
import { Avatar } from '../../components/Avatar.js';
import { useApi } from '../../components/api-context.js';
import { SlideOver } from '../../panels/SlideOver.js';
import { useToast } from '../../toast.js';

interface AddContactsPanelProps {
  open: boolean;
  onClose(): void;
  /** Set of contactIds already in the list, so we can disable their rows. */
  existing: Set<string>;
  onAdd(contactIds: string[]): Promise<void>;
}

export function AddContactsPanel({ open, onClose, existing, onAdd }: AddContactsPanelProps) {
  const api = useApi();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Contact[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  // Debounce the URL-free query → list() call.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => setQuery(draft), 300);
    return () => window.clearTimeout(id);
  }, [draft, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setResults(null);
    api
      .list({ query: query || undefined, kind: 'person' })
      .then((res) => {
        if (!cancelled) setResults(res.items);
      })
      .catch((err: Error) => {
        if (!cancelled) toast.error(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, query, toast]);

  useEffect(() => {
    if (!open) {
      setPicked(new Set());
      setDraft('');
      setQuery('');
    }
  }, [open]);

  function toggle(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  }

  async function commit() {
    const ids = [...picked];
    if (ids.length === 0) return;
    setAdding(true);
    try {
      await onAdd(ids);
      onClose();
    } finally {
      setAdding(false);
    }
  }

  return (
    <SlideOver open={open} onClose={onClose} ariaLabel="Add contacts to list" width="28rem">
      <div className="flex items-center gap-2 border-b border-divider px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted hover:surface-hover"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <p className="text-sm font-semibold text-strong flex-1">Add to dial list</p>
        <button
          type="button"
          onClick={commit}
          disabled={adding || picked.size === 0}
          className="btn-primary px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          {adding ? 'Adding…' : `Add ${picked.size || ''}`.trim()}
        </button>
      </div>
      <div className="p-3 border-b border-divider">
        <input
          autoFocus
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search people…"
          className="input w-full"
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {results === null ? (
          <p className="p-4 text-sm text-muted">Loading…</p>
        ) : results.length === 0 ? (
          <p className="p-4 text-sm text-muted">No matches.</p>
        ) : (
          <ul>
            {results.map((c) => {
              const already = existing.has(c.id);
              const isPicked = picked.has(c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => !already && toggle(c.id)}
                    disabled={already}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-divider last:border-b-0 transition ${
                      already
                        ? 'opacity-50 cursor-not-allowed'
                        : isPicked
                          ? 'surface-muted'
                          : 'hover:surface-hover'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={already || isPicked}
                      readOnly
                      disabled={already}
                      className="h-4 w-4 cursor-pointer accent-[color:var(--cc-accent)]"
                    />
                    <Avatar name={c.displayName} photoUrl={c.photoUrl} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-strong truncate">{c.displayName}</p>
                      <p className="text-xs text-muted truncate">
                        {c.phoneNumbers[0]?.e164 ??
                          c.phoneNumbers[0]?.number ??
                          c.emails[0]?.address ??
                          '—'}
                      </p>
                    </div>
                    {already && <span className="text-xs text-faint">in list</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SlideOver>
  );
}
